import { defineStore } from "pinia";
import { checkIfWorkspaceAvailableForCustomer, getWorkspaceFromRedirect } from "@/_helpers/utils";
import type { WorkspaceDetails } from "@/_store/workspaces-list.module.ts";
import router from "@/_helpers/router";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import { StoreModule } from "@/constants/store.ts";

interface RedirectsState {
  redirectUrl: string;
  redirectPath: string;
  shouldHandleSocAutomationRedirect: boolean;
  socAutomationAction: string;
  socAutomationPolicyType: string;
  socAutomationEnrollmentCode: string;
  socAutomationOsType: string;
  shouldHandleDashboardRedirect: boolean;
  workspace: Partial<WorkspaceDetails>;
  workspaceId: string;
  navigationFromSocAutomation: boolean;
  navigationFromReport: boolean;
}

export interface SocAutomationParams {
  workspaceId: string;
  action: string;
  policyType: string;
  enrollmentCode: string;
  osType: string;
}

const defaultState: RedirectsState = {
  redirectUrl: "",
  redirectPath: "",
  shouldHandleSocAutomationRedirect: false,
  socAutomationAction: "",
  socAutomationPolicyType: "",
  socAutomationEnrollmentCode: "",
  socAutomationOsType: "",
  shouldHandleDashboardRedirect: false,
  workspace: {},
  navigationFromSocAutomation: false,
  navigationFromReport: false,
  workspaceId: "",
};

export const useRedirectsStore = defineStore(StoreModule.REDIRECTS, {
  state: (): RedirectsState =>
    cloneDeep({
      ...defaultState,
    }),
  persist: true,
  getters: {
    hasNavigationFromEmail: (state) =>
      state.navigationFromReport || state.navigationFromSocAutomation,
  },
  actions: {
    async handleRedirectFromReport() {
      const savedRedirectUrl = this.redirectUrl;
      const navigationFromReport = this.navigationFromReport;
      if (savedRedirectUrl && navigationFromReport) {
        const workspace = this.workspace;
        const useWorkspaceFromReport = await checkIfWorkspaceAvailableForCustomer(
          workspace?.workspaceId!
        );
        if (useWorkspaceFromReport && workspace && workspace.workspaceId && workspace.name) {
          const workspaceDetails = await getWorkspaceFromRedirect(workspace.workspaceId);
          await this.setWorkspace(workspaceDetails!);
          localStorage.setItem("report-navigation", `reportNav${Math.random()}`);
          if (!savedRedirectUrl.startsWith("/portal/tickets")) {
            router.replace(this.redirectPath).catch(() => {});
          }
          this.$reset();
        }
      }
    },
    async handleRedirectFromSocAutomation({
      workspaceId,
      action,
      policyType,
      enrollmentCode,
      osType,
    }: SocAutomationParams) {
      if (!workspaceId) return;
      const workspace = await getWorkspaceFromRedirect(workspaceId);
      this.shouldHandleSocAutomationRedirect = Boolean(workspace);
      const navigationFromSocAutomation = this.navigationFromSocAutomation;
      if (workspace && navigationFromSocAutomation) {
        this.workspaceId = workspaceId;
        this.socAutomationAction = action;
        this.socAutomationPolicyType = policyType;
        this.socAutomationEnrollmentCode = enrollmentCode;
        this.socAutomationOsType = osType;
        await this.setWorkspace(workspace);
        localStorage.setItem("report-navigation", `reportNav${Math.random()}`);
      }
    },
    async handleSocAutomationAfterLogin() {
      const savedRedirectUrl = this.redirectUrl;
      const navigationFromSocAutomation = this.navigationFromSocAutomation;
      if (savedRedirectUrl && navigationFromSocAutomation) {
        const workspace = await getWorkspaceFromRedirect(this.workspaceId);
        this.shouldHandleSocAutomationRedirect = Boolean(workspace);
        if (workspace) {
          await this.setWorkspace(workspace);
          await router.push(this.redirectUrl);
          localStorage.setItem("report-navigation", `reportNav${Math.random()}`);
        }
      }
    },
    async handleRedirectFromReportAfterLogin() {
      const savedRedirectUrl = this.redirectUrl;
      const navigationFromReport = this.navigationFromReport;
      if (savedRedirectUrl && navigationFromReport) {
        const workspace = this.workspace;
        const useWorkspaceFromReport = await checkIfWorkspaceAvailableForCustomer(
          workspace?.workspaceId!
        );
        if (useWorkspaceFromReport && !isEmpty(workspace)) {
          const workspaceDetails = await getWorkspaceFromRedirect(workspace.workspaceId!);
          await this.setWorkspace(workspaceDetails!);
          localStorage.setItem("report-navigation", `reportNav${Math.random()}`);
          router.push(this.redirectUrl).catch(() => {});
          if (!savedRedirectUrl.startsWith("/portal/tickets")) {
            router.replace(this.redirectPath).catch(() => {});
          }
          this.$reset();
        }
      }
    },
    async handleRedirectToDashboard(workspaceId: string) {
      const workspace = await getWorkspaceFromRedirect(workspaceId);
      this.shouldHandleSocAutomationRedirect = Boolean(workspace);
      if (workspace) {
        await this.setWorkspace(workspace);
        localStorage.setItem("report-navigation", `reportNav${Math.random()}`);
      }
    },
    resetRedirects() {
      this.$reset();
    },
    async setWorkspace(workspace: Partial<WorkspaceDetails>) {
      const workspaceStore = useWorkspaceStore();
      useAppsManagementModule().handleAppsManagement(workspace.workspaceId!, false);
      workspaceStore.setWorkspace(workspace as WorkspaceDetails);
      await workspaceStore.getCurrentWorkspace();
    },
  },
});
